import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'react-toastify';
import MainLayout from '../../components/Layout/MainLayout';
import reportsService from '../../services/reportsService';
import { exportToCSV, exportToPDF } from '../../utils/reports/exportUtils';

const HRPerformanceReportPage = () => {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    page: 1,
    limit: 10
  });
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ data: [], pagination: {} });
  const [topPerformer, setTopPerformer] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getHRPerformanceReport(filters);
      if (response.success) {
        setPayload(response.data);
        setTopPerformer(response.data.topPerformer);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load HR performance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.month, filters.year, filters.page, filters.limit]);

  const chartData = useMemo(() => payload.data.map((item) => ({
    name: item.hrName,
    selectionRate: item.selectionRate
  })), [payload.data]);

  const onExportCSV = () => {
    exportToCSV(
      `hr-performance-${filters.month}-${filters.year}`,
      [
        { label: 'HR Name', key: 'hrName' },
        { label: 'Working Days', key: 'totalWorkingDays' },
        { label: 'Present Days', key: 'presentDays' },
        { label: 'Leaves', key: 'leavesTaken' },
        { label: 'Interviews', key: 'interviewsConducted' },
        { label: 'Candidates Handled', key: 'candidatesHandled' },
        { label: 'Selected', key: 'selectedCount' },
        { label: 'Selection Rate %', key: 'selectionRate' }
      ],
      payload.data
    );
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="text-3xl font-bold text-charcoal">HR Performance Report</h1>
          <div className="flex gap-2">
            <button onClick={onExportCSV} className="px-4 py-2 bg-olive text-white rounded-lg text-sm">Export CSV</button>
            <button onClick={exportToPDF} className="px-4 py-2 bg-charcoal text-white rounded-lg text-sm">Export PDF</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="number"
            min="1"
            max="12"
            value={filters.month}
            onChange={(e) => setFilters((p) => ({ ...p, month: Number(e.target.value), page: 1 }))}
            className="border rounded-lg px-3 py-2"
            placeholder="Month"
          />
          <input
            type="number"
            min="2000"
            value={filters.year}
            onChange={(e) => setFilters((p) => ({ ...p, year: Number(e.target.value), page: 1 }))}
            className="border rounded-lg px-3 py-2"
            placeholder="Year"
          />
          <select
            value={filters.limit}
            onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))}
            className="border rounded-lg px-3 py-2"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>
          {topPerformer ? (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <p className="text-xs text-amber-700">Top Performing HR</p>
              <p className="text-sm font-semibold text-amber-900">{topPerformer.hrName} ({topPerformer.selectionRate}%)</p>
            </div>
          ) : <div />}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-charcoal mb-4">Selection Rate by HR</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="selectionRate" fill="#3B82F6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>HR</Th><Th>Working Days</Th><Th>Present</Th><Th>Leaves</Th><Th>Interviews</Th><Th>Candidates</Th><Th>Selected</Th><Th>Selection %</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-6 text-center" colSpan={8}>Loading...</td></tr>
              ) : payload.data.length === 0 ? (
                <tr><td className="px-4 py-6 text-center" colSpan={8}>No records found</td></tr>
              ) : payload.data.map((row) => (
                <tr key={row.hrId} className="border-t">
                  <Td>{row.hrName}</Td><Td>{row.totalWorkingDays}</Td><Td>{row.presentDays}</Td><Td>{row.leavesTaken}</Td>
                  <Td>{row.interviewsConducted}</Td><Td>{row.candidatesHandled}</Td><Td>{row.selectedCount}</Td><Td>{row.selectionRate}%</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination pagination={payload.pagination} onPageChange={(nextPage) => setFilters((p) => ({ ...p, page: nextPage }))} />
      </div>
    </MainLayout>
  );
};

const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs uppercase text-gray-600">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

const Pagination = ({ pagination, onPageChange }) => (
  <div className="flex items-center justify-between">
    <p className="text-sm text-gray-600">Page {pagination?.page || 1} of {pagination?.totalPages || 1}</p>
    <div className="flex gap-2">
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        disabled={!pagination?.page || pagination.page <= 1}
        onClick={() => onPageChange(pagination.page - 1)}
      >Previous</button>
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        disabled={!pagination?.page || pagination.page >= pagination.totalPages}
        onClick={() => onPageChange(pagination.page + 1)}
      >Next</button>
    </div>
  </div>
);

export default HRPerformanceReportPage;
